import { useMemo } from 'react';

import { PropertyRow } from '@/components/workbench';
import { useAdCopyPlatforms } from '@/hooks/useAdCopyPlatforms';
import { useBriefWorkspace } from '@/hooks/useBriefWorkspace';
import { useContentAngleById, useContentAngles } from '@/hooks/useContentAngles';
import type { ConceptFormat, ConceptProof } from '@/lib/briefs/concept-contract';
import { CONCEPT_FORMATS } from '@/lib/briefs/concept-contract';
import { SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import type { BriefConceptWrite } from '@/services/data/BriefConceptEditorService';
import { getPlatformLabel } from '@/utils/platformUtils';

/**
 * THE CONCEPT'S OWN FIELDS, in the inspector, EDITABLE.
 *
 * Part 1 removed "Audience set" / "Angle set" from the header, and it was right
 * to: they were static `<span>`s wearing `SELECTABLE_PILL`, a token whose own
 * doc scopes it to toggles. But removing them left the two fields the concept
 * contract names FIRST with no UI anywhere in this surface — a header badge that
 * lied replaced by nothing at all. These are the real columns
 * (`project_tasks.audience_id` / `.angle_id`) on the same draft the brief Sheet
 * writes, through the same version-checked service, and they are dropdowns
 * because they are choices.
 *
 * WHY HERE AND NOT IN THE BRIEF SHEET. The Sheet holds PROSE — subject, setting,
 * rationale, message — which needs a measure. These are enumerable values that
 * read at a glance, which is exactly what the label-left/value-right property
 * row is for, and they belong in the column the user is already reading while
 * choosing a size and a headline. Ordering follows the rail's outside-in rule:
 * Size variants (delivery) → Concept (who and why) → Content (what it says) →
 * layer sections (how it looks).
 *
 * ANGLE LABELS ARE THE LANE, NOT THE STATEMENT. A `content_angles.statement` runs
 * ~200 characters ("For Busy Professionals at D2, lead with wanting a caffeine
 * boost without the coffee-shop line, framed as…"). Right-aligned in a 400px
 * rail that truncates to nothing useful, so the row shows the lane and the menu
 * shows the whole statement underneath it. The list is narrowed to the angles
 * researched for THIS concept's persona, because an angle belongs to the
 * audience it was written for — the same coupling `BriefConceptPage` enforces by
 * clearing `angleId` whenever the persona changes.
 */

const PROOF_KINDS: Array<{ value: ConceptProof['kind']; label: string }> = [
  { value: 'none', label: 'No proof yet' },
  { value: 'metric', label: 'Metric' },
  { value: 'quote', label: 'Quote' },
  { value: 'case', label: 'Case study' },
  { value: 'artefact', label: 'Artifact' },
];

/**
 * Delivery is stored as an ARRAY of intents, and a dropdown holds one value, so
 * the three states the array can actually be in are enumerated instead of
 * pretending a multi-select fits a property row. Empty is not offered: the
 * editor's own rule is "keep at least one delivery intent selected", and this
 * drives `defaultRatiosForPlatforms`, so a concept with no intent has no
 * recommended sizes.
 */
const DELIVERY_OPTIONS = [
  { value: 'organic', label: 'Organic', description: 'Posted to a social account' },
  { value: 'paid', label: 'Paid', description: 'Runs as an ad' },
  { value: 'both', label: 'Organic + paid', description: 'Ships to both, from one concept' },
];

/**
 * Sentinel for "no paid platform chosen yet" — `PropertyRow`'s dropdown
 * variant needs a defined `value`, and `null`/`''` aren't valid option values.
 */
const PAID_PLATFORM_UNSET = 'unset';

const LANE_LABELS: Record<string, string> = {
  the_mistake: 'The mistake',
  point_of_view: 'Point of view',
  mechanism: 'Mechanism',
  comparison: 'Comparison',
  social_proof: 'Social proof',
  bold_claim: 'Bold claim',
};

function laneLabel(lane: string): string {
  return (
    LANE_LABELS[lane] ??
    lane.replace(/[_-]+/g, ' ').replace(/^./, (character) => character.toUpperCase())
  );
}

function formatLabel(format: ConceptFormat): string {
  if (format === null) return 'Not set';
  return format.charAt(0).toUpperCase() + format.slice(1);
}

const ROW_TOUCH = 'max-md:min-h-11';

export interface ConceptFieldsSectionProps {
  briefId: string;
  draft: BriefConceptWrite;
  onChange: (patch: Partial<BriefConceptWrite>) => void;
  disabled?: boolean;
  className?: string;
  /**
   * Which rows render. `'all'` (the default) is the inspector's own column,
   * unchanged. `'plan'` and `'proof'` exist because the Concept page splits the
   * same rows across two authoring stages — who this is for, and what backs it
   * up — and a second copy of these rows written against the same columns is
   * exactly the fork this file was extracted to prevent.
   */
  rows?: 'all' | 'plan' | 'proof';
  /**
   * `'section'` (the default) keeps this component's own hairline box and
   * `Concept` eyebrow. `'bare'` hands the header to the caller — the Concept
   * page's stage header already names the group, and a second heading inside it
   * is a second type system on one screen.
   */
  chrome?: 'section' | 'bare';
  writtenPaidPlatformLabel?: string | null;
}

export function ConceptFieldsSection({
  briefId,
  draft,
  onChange,
  disabled = false,
  className,
  rows = 'all',
  chrome = 'section',
  writtenPaidPlatformLabel = null,
}: ConceptFieldsSectionProps): JSX.Element {
  const workspaceQuery = useBriefWorkspace(briefId);
  const { angles } = useContentAngles();
  const angleListed = !draft.angleId || angles.some((angle) => angle.id === draft.angleId);
  const { angle: ownAngle, isLoading: ownAngleLoading } = useContentAngleById(
    draft.angleId,
    !angleListed,
  );
  const { data: adCopyPlatforms } = useAdCopyPlatforms();

  const personaOptions = useMemo(
    () => [
      { value: 'unassigned', label: 'Unassigned' },
      ...(workspaceQuery.data?.personas ?? []).map((persona) => ({
        value: persona.id,
        label: persona.name,
      })),
    ],
    [workspaceQuery.data],
  );

  const angleOptions = useMemo(() => {
    const forPersona = angles.filter(
      (angle) => !draft.audienceId || angle.audienceId === draft.audienceId,
    );
    const options = [
      { value: 'unassigned', label: 'No angle', description: 'This concept leads with the hook' },
      ...forPersona.map((angle) => ({
        value: angle.id,
        label: laneLabel(angle.lane),
        description: angle.statement,
      })),
    ];
    /**
     * `useContentAngles` collapses duplicate statements across brief runs and
     * narrows to 60 rows, so the angle a concept was ORDERED with can be absent
     * from the list. Without this the row would print a raw uuid as its value —
     * a property row confidently showing a database key to a marketer. Name the
     * state instead.
     */
    if (draft.angleId && !options.some((option) => option.value === draft.angleId)) {
      const own = angles.find((angle) => angle.id === draft.angleId) ?? ownAngle;
      options.splice(
        1,
        0,
        own
          ? {
              value: own.id,
              label: laneLabel(own.lane),
              description: own.statement,
            }
          : {
              value: draft.angleId,
              label: ownAngleLoading ? 'Loading angle' : 'Angle not found',
              description: ownAngleLoading
                ? 'Reading this concept’s angle.'
                : 'This angle is no longer on file for this workspace.',
            },
      );
    }
    return options;
  }, [angles, ownAngle, ownAngleLoading, draft.audienceId, draft.angleId]);

  /**
   * `public.ad_copy_platforms` (via `useAdCopyPlatforms`), turned into
   * `PropertyRow` dropdown options — the same table
   * `select_brief_concept_copy_revision` itself reads `supports_copy_revision`
   * from (see `AdCopyPlatformOption`'s doc comment). LinkedIn flipped to
   * `true` 2026-09-24 (Lane R3-1); this description now renders only for a
   * platform the registry still marks unsupported (Google RSA today). This
   * replaced a free-text field precisely because free text could say
   * "LinkedIn" with no indication of whether that platform's copy could reach
   * paid delivery — 9 of 17 paid-intent Briefs did exactly that in
   * production before this fix (read 2026-08-31). A stray legacy value that
   * predates the registry (case-different text, a platform since retired)
   * still renders — `PropertyRow`'s dropdown falls back to the raw string
   * when nothing matches — so an old Concept is never silently rewritten by
   * opening this panel.
   *
   * 🔴 The description is a FIXED sentence, never `platform.notes` — caught
   * live on `/briefs/:id`, 2026-08-31. `notes` is written for an engineer
   * reading a migration ("flip supports_copy_revision once the ledger
   * accepts LinkedIn copy shapes") and reads as jargon to the marketer this
   * dropdown is actually for. Keep the registry as the SOURCE OF TRUTH for
   * which platforms exist and which support copy revision; write this
   * surface's own words for what a person reads.
   */
  const paidPlatformOptions = useMemo(() => {
    const registryOptions = (adCopyPlatforms ?? []).map((platform) => ({
      value: platform.id,
      label: platform.label,
      description: platform.supportsCopyRevision
        ? undefined
        : 'Copy only. It cannot be made the current copy yet.',
    }));
    const storedPaid = draft.intents.find((intent) => intent.intent === 'paid')?.platform?.trim();
    const storedOption =
      storedPaid && !registryOptions.some((option) => option.value === storedPaid)
        ? [{ value: storedPaid, label: getPlatformLabel(storedPaid), description: undefined }]
        : [];
    return [
      {
        value: PAID_PLATFORM_UNSET,
        label: writtenPaidPlatformLabel
          ? `Not set · copy written for ${writtenPaidPlatformLabel}`
          : 'Not set',
      },
      ...registryOptions,
      ...storedOption,
    ];
  }, [adCopyPlatforms, writtenPaidPlatformLabel, draft.intents]);

  const organic = draft.intents.find((intent) => intent.intent === 'organic');
  const paid = draft.intents.find((intent) => intent.intent === 'paid');
  const delivery = organic && paid ? 'both' : paid ? 'paid' : 'organic';

  const setDelivery = (next: string) => {
    const keep = (intent: 'organic' | 'paid') =>
      draft.intents.find((entry) => entry.intent === intent) ?? { intent, platform: null };
    if (next === 'both') {
      onChange({ intents: [keep('organic'), keep('paid')] });
      return;
    }
    onChange({ intents: [keep(next === 'paid' ? 'paid' : 'organic')] });
  };

  const setPlatform = (intent: 'organic' | 'paid', platform: string) => {
    onChange({
      intents: draft.intents.map((entry) =>
        entry.intent === intent ? { ...entry, platform: platform || null } : entry,
      ),
    });
  };

  const showPlan = rows === 'all' || rows === 'plan';
  const showProof = rows === 'all' || rows === 'proof';

  const body = (
    <>
      {showPlan ? (
        <PropertyRow
          className={chrome === 'bare' ? ROW_TOUCH : undefined}
          variant="dropdown"
          label="Persona"
          disabled={disabled}
          value={draft.audienceId ?? 'unassigned'}
          options={personaOptions}
          onChange={(value) =>
            onChange({
              audienceId: value === 'unassigned' ? null : value,
              // An angle belongs to its original persona. Carrying a stale one
              // across is how a concept ends up arguing to the wrong audience.
              angleId: null,
            })
          }
        />
      ) : null}

      {showPlan ? (
        <PropertyRow
          className={chrome === 'bare' ? ROW_TOUCH : undefined}
          variant="dropdown"
          label="Angle"
          disabled={disabled}
          value={draft.angleId ?? 'unassigned'}
          options={angleOptions}
          onChange={(value) => onChange({ angleId: value === 'unassigned' ? null : value })}
        />
      ) : null}

      {/* Delivery drives this surface's OWN defaults — `defaultRatiosForPlatforms`
          picks the recommended ratios from it and the copy limits are checked
          against it — and until now the user could not see, let alone change,
          why 1:1/4:5/9:16 were the sizes on offer. */}
      {showPlan ? (
        <PropertyRow
          className={chrome === 'bare' ? ROW_TOUCH : undefined}
          variant="dropdown"
          label="Delivery"
          disabled={disabled}
          value={delivery}
          options={DELIVERY_OPTIONS}
          onChange={setDelivery}
        />
      ) : null}

      {showPlan && organic ? (
        <PropertyRow
          className={chrome === 'bare' ? ROW_TOUCH : undefined}
          variant="input"
          label="Organic on"
          disabled={disabled}
          placeholder="Any platform"
          value={organic.platform ?? ''}
          onChange={(value) => setPlatform('organic', value)}
        />
      ) : null}

      {showPlan && paid ? (
        <PropertyRow
          className={chrome === 'bare' ? ROW_TOUCH : undefined}
          variant="dropdown"
          label="Paid on"
          disabled={disabled}
          value={paid.platform ?? PAID_PLATFORM_UNSET}
          options={paidPlatformOptions}
          onChange={(value) => setPlatform('paid', value === PAID_PLATFORM_UNSET ? '' : value)}
        />
      ) : null}

      {showPlan ? (
        <PropertyRow
          className={chrome === 'bare' ? ROW_TOUCH : undefined}
          variant="dropdown"
          label="Format"
          disabled={disabled}
          value={draft.format ?? 'unset'}
          options={[
            { value: 'unset', label: 'Not set' },
            ...CONCEPT_FORMATS.map((format) => ({ value: format, label: formatLabel(format) })),
          ]}
          onChange={(value) =>
            onChange({
              format: value === 'unset' ? null : (value as Exclude<ConceptFormat, null>),
            })
          }
        />
      ) : null}

      {/* Proof KIND and SOURCE, not the quote. The quote is prose and lives with
          the other prose; the kind is what makes the quote mean something, and
          editing a testimonial in a rail that still believes `kind='metric'` is
          how evidence gets silently misrepresented. */}
      {showProof ? (
        <PropertyRow
          className={chrome === 'bare' ? ROW_TOUCH : undefined}
          variant="dropdown"
          label="Proof"
          disabled={disabled}
          value={draft.proof.kind}
          options={PROOF_KINDS}
          onChange={(value) => {
            const kind = value as ConceptProof['kind'];
            if (kind === 'none') {
              onChange({ proof: { kind: 'none', ref: null, quote: null } });
              return;
            }
            onChange({
              proof: {
                kind,
                ref: draft.proof.kind === 'none' ? null : draft.proof.ref,
                quote: draft.proof.kind === 'none' ? '' : draft.proof.quote,
              },
            });
          }}
        />
      ) : null}

      {showProof && draft.proof.kind !== 'none' ? (
        <PropertyRow
          className={chrome === 'bare' ? ROW_TOUCH : undefined}
          variant="input"
          label="Source"
          disabled={disabled}
          placeholder="Where it came from"
          value={draft.proof.ref ?? ''}
          onChange={(value) => {
            if (draft.proof.kind === 'none') return;
            onChange({
              proof: { kind: draft.proof.kind, ref: value || null, quote: draft.proof.quote },
            });
          }}
        />
      ) : null}
    </>
  );

  if (chrome === 'bare') {
    return <div className={cn('flex flex-col gap-1.5', className)}>{body}</div>;
  }

  return (
    <section
      className={cn('flex flex-col gap-1.5 border-b border-border/40 px-2.5 py-2.5', className)}
    >
      <header className="flex items-center justify-between gap-2 pb-0.5">
        <span className={SECTION_LABEL}>Concept</span>
      </header>
      {body}
    </section>
  );
}
